<template>
	<div>
		<b-card no-body class="mb-0">
			<div class="m-2">
				<b-row>
					<b-col cols="12">
						<h4 class="mb-0">Configuración de Aprobaciones por Departamento</h4>
						<small class="text-muted">
							Defina los departamentos de aprobación por defecto para Jefatura y Superintendencia.
						</small>
					</b-col>
				</b-row>
			</div>

			<b-table
				ref="refApprovalConfigTable"
				class="position-relative"
				:items="departments"
				:fields="tableFields"
				responsive
				show-empty
				empty-text="No se encontraron registros"
				bordered
			>
				<template #table-busy>
					<div class="text-center text-primary my-2">
						<b-spinner class="align-middle mb-1"></b-spinner>
						<div>
							<b>CARGANDO</b>
						</div>
					</div>
				</template>

				<template #cell(department_name)="data">
					<strong>{{ data.item.department_name }}</strong>
				</template>

				<template #cell(leadership_department)="data">
					<div class="d-flex align-items-center">
						<v-select
							v-model="data.item.leadership_department"
							:options="departmentOptions"
							placeholder="Seleccione departamento"
							class="flex-grow-1"
							@input="onChangeApprovalDept(data.item, 1, $event)"
						/>
						<b-button
							v-if="data.item.leadership_department"
							variant="flat-info"
							class="btn-icon ml-50"
							size="sm"
							v-b-tooltip.hover
							title="Ver usuarios con acceso de Jefatura"
							@click="openUsersModal(data.item.leadership_department, 'Jefatura')"
						>
							<feather-icon icon="EyeIcon" />
						</b-button>
					</div>
				</template>

				<template #cell(superintendence_department)="data">
					<div class="d-flex align-items-center">
						<v-select
							v-model="data.item.superintendence_department"
							:options="departmentOptions"
							placeholder="Seleccione departamento"
							class="flex-grow-1"
							@input="onChangeApprovalDept(data.item, 2, $event)"
						/>
						<b-button
							v-if="data.item.superintendence_department"
							variant="flat-info"
							class="btn-icon ml-50"
							size="sm"
							v-b-tooltip.hover
							title="Ver usuarios con acceso de Superintendencia"
							@click="openUsersModal(data.item.superintendence_department, 'Superintendencia')"
						>
							<feather-icon icon="EyeIcon" />
						</b-button>
					</div>
				</template>

				<template #cell(actions)="data">
					<b-button
						variant="flat-secondary"
						class="btn-icon"
						size="sm"
						v-b-tooltip.hover
						title="Ver historial de cambios"
						@click="openHistoryModal(data.item)"
					>
						<feather-icon icon="ClockIcon" />
					</b-button>
				</template>
			</b-table>
		</b-card>

		<!-- Modal: Ver Usuarios -->
		<b-modal
			ref="refUsersModal"
			:title="usersModalTitle"
			title-tag="div"
			header-bg-variant="primary"
			header-text-variant="white"
			hide-footer
			no-close-on-backdrop
			no-close-on-esc
			size="lg"
		>
			<b-table
				:fields="usersFields"
				:items="usersItems"
				empty-text="No se encontraron usuarios"
				show-empty
				responsive
				bordered
			>
				<template #table-busy>
					<div class="text-center text-primary my-2">
						<b-spinner class="align-middle mb-1"></b-spinner>
						<div>
							<b>CARGANDO</b>
						</div>
					</div>
				</template>

				<template #head(has_access)="data">
					<span v-html="data.label"></span>
				</template>

				<template #cell(has_access)="data">
					<div class="text-nowrap text-center">
						<b-button
							:variant="`flat-${data.item.has_access ? 'success' : 'danger'}`"
							class="btn-icon rounded-circle"
						>
							<feather-icon :icon="data.item.has_access ? 'CheckCircleIcon' : 'XCircleIcon'" />
						</b-button>
					</div>
				</template>
			</b-table>
		</b-modal>

		<!-- Modal: Historial de Cambios -->
		<b-modal
			ref="refHistoryModal"
			:title="historyModalTitle"
			title-tag="div"
			header-bg-variant="primary"
			header-text-variant="white"
			hide-footer
			no-close-on-backdrop
			no-close-on-esc
			size="xl"
		>
			<b-table
				:fields="historyFields"
				:items="historyItems"
				empty-text="No se encontraron registros de historial"
				show-empty
				responsive
				bordered
			>
				<template #table-busy>
					<div class="text-center text-primary my-2">
						<b-spinner class="align-middle mb-1"></b-spinner>
						<div>
							<b>CARGANDO</b>
						</div>
					</div>
				</template>

				<template #cell(created_at)="data">
					<span class="text-nowrap">{{ formatStringToDate(data.item.created_at) }}</span>
				</template>
			</b-table>
		</b-modal>
	</div>
</template>

<script>
import approvalConfigStoreModule from "./approvalConfigStoreModule"
import { formatStringToDate } from "@core/utils/filter"
import useNotifications from "@notifications"
import { ref, onMounted, onUnmounted } from "@vue/composition-api"
import { VBTooltip } from "bootstrap-vue"
import vSelect from "vue-select"
import store from "@/store"
import axios from "@axios"

export default {
	components: {
		vSelect
	},
	directives: {
		"b-tooltip": VBTooltip
	},
	setup() {
		const { toastNotification } = useNotifications()

		const STORE_NAME = "app-approval-config"

		if (!store.hasModule(STORE_NAME))
			store.registerModule(STORE_NAME, approvalConfigStoreModule)

		onUnmounted(() => {
			if (store.hasModule(STORE_NAME))
				store.unregisterModule(STORE_NAME)
		})

		// Main table
		const refApprovalConfigTable = ref(null)
		const departments = ref([])
		const departmentOptions = ref([])
		const tableFields = [
			{ key: "department_name", label: "Departamento", thClass: "text-center", tdClass: "align-middle", thStyle: { width: "20%" } },
			{ key: "leadership_department", label: "Dpto. Aprobación Jefatura", thClass: "text-center", thStyle: { width: "30%" } },
			{ key: "superintendence_department", label: "Dpto. Aprobación Superintendencia", thClass: "text-center", thStyle: { width: "30%" } },
			{ key: "actions", label: "Historial", thClass: "text-center", tdClass: "text-center", thStyle: { width: "10%" } }
		]

		// Users modal
		const refUsersModal = ref(null)
		const usersModalTitle = ref("")
		const usersFields = ref([])
		const usersItems = ref([])

		// History modal
		const refHistoryModal = ref(null)
		const historyModalTitle = ref("")
		const historyFields = [
			{ key: "created_at", label: "Fecha", thClass: "text-center", tdClass: "text-center" },
			{ key: "field_changed", label: "Campo Modificado", thClass: "text-center", tdClass: "text-center" },
			{ key: "old_value", label: "Valor Anterior", thClass: "text-center", tdClass: "text-center" },
			{ key: "new_value", label: "Valor Nuevo", thClass: "text-center", tdClass: "text-center" },
			{ key: "changed_by", label: "Modificado Por", thClass: "text-center", tdClass: "text-center" }
		]
		const historyItems = ref([])

		const getDepartmentOptions = async () => {
			try {
				const { data } = await axios.get("/selectors/departments")
				departmentOptions.value = data.departments
			} catch (error) {
				toastNotification(error.response?.data?.message, false)
			}
		}

		const getApprovalDefaults = async () => {
			try {
				const response = await store.dispatch(`${STORE_NAME}/fetchApprovalDefaults`)
				departments.value = response.data.departments.map(dept => {
					const selfDept = departmentOptions.value.find(d => d.value === dept.department_id) || null

					return {
						...dept,
						leadership_department: dept.leadership_department_id
							? departmentOptions.value.find(d => d.value === dept.leadership_department_id) || selfDept
							: selfDept,
						superintendence_department: dept.superintendence_department_id
							? departmentOptions.value.find(d => d.value === dept.superintendence_department_id) || selfDept
							: selfDept
					}
				})
			} catch (error) {
				toastNotification(error.response?.data?.message, false)
			}
		}

		const onChangeApprovalDept = async (department, approvalLevelId, selected) => {
			if (!selected) return
			try {
				const payload = {
					department_id: department.department_id,
					approval_level_id: approvalLevelId,
					target_department_id: selected.value
				}
				const response = await store.dispatch(`${STORE_NAME}/updateApprovalDefault`, payload)
				toastNotification(response.data.message || "Configuración actualizada correctamente")
			} catch (error) {
				toastNotification(error.response?.data?.message, false)
				await getApprovalDefaults()
			}
		}

		const openUsersModal = async (selectedDept, accessLevelLabel) => {
			usersModalTitle.value = `USUARIOS CON ACCESO DE ${accessLevelLabel.toUpperCase()} - ${selectedDept.label}`
			try {
				const { data } = await axios.get(
					`/users/access?department_ids=${selectedDept.value}`
				)

				const accessKey = accessLevelLabel === "Jefatura" ? "is_leadership" : "is_superintendence"

				usersFields.value = [
					{ key: "department", label: "Área", thClass: "text-center" },
					{ key: "user", label: "Usuario", thClass: "text-center" },
					{ key: "has_access", label: accessLevelLabel.toUpperCase(), thClass: "text-center", tdClass: "text-center" }
				]

				usersItems.value = (data.userAccess?.items || [])
					.filter(item => !item.is_admin)
					.map((item, index) => ({
						...item,
						id: index + 1,
						has_access: item[accessKey]
					}))

				refUsersModal.value.show()
			} catch (error) {
				toastNotification(error.response?.data?.message, false)
			}
		}

		const openHistoryModal = async (department) => {
			historyModalTitle.value = `HISTORIAL DE CAMBIOS - ${department.department_name}`
			try {
				const response = await store.dispatch(`${STORE_NAME}/fetchApprovalHistory`, { id: department.department_id })
				historyItems.value = response.data.history || []
				refHistoryModal.value.show()
			} catch (error) {
				toastNotification(error.response?.data?.message, false)
			}
		}

		onMounted(async () => {
			await getDepartmentOptions()
			await getApprovalDefaults()
		})

		return {
			refApprovalConfigTable,
			departments,
			departmentOptions,
			tableFields,

			refUsersModal,
			usersModalTitle,
			usersFields,
			usersItems,

			refHistoryModal,
			historyModalTitle,
			historyFields,
			historyItems,

			onChangeApprovalDept,
			openUsersModal,
			openHistoryModal,
			formatStringToDate
		}
	}
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
